import React, {useState} from 'react';
import {useDropzone} from 'react-dropzone';
import Icon from 'shared/components/icon';
import Text from 'shared/components/text';
import {useMediaQuery} from 'src/hooks/useMediaQuery';
import 'stylesheets/components/educator-account-settings/educator-account-settings-form.scss';
import styles from './official-id-uploader.module.scss';

interface Props {
  image?: string;
  uploadOfficialId: (file: File) => void;
  uploading: boolean;
  editing: boolean;
}

const OfficialIdUploader = ({image, uploadOfficialId, uploading, editing}: Props) => {
  const [hover, setHover] = useState(false);
  const mq = useMediaQuery();

  const uploadImage = ([file]: File[]) => {
    if (!file) {
      return;
    }
    uploadOfficialId(file);
  };

  const {getRootProps, getInputProps} = useDropzone({
    accept: {'image/*': []},
    multiple: false,
    onDrop: uploadImage,
  });

  const renderOverlay = () => {
    if (!editing && !uploading && !hover) {
      return null;
    }

    return (
      <div className="avatar-overlay">
        <div className="avatar-overlay-image">
          {uploading ? (
            <Icon iconType="spinner" className="avatar-spinner rm-spin" />
          ) : (
            <div>
              <svg
                width="28px"
                height="34px"
                viewBox="0 0 28 34"
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                  <g transform="translate(-229.000000, -183.000000)" fill="#FFFFFF">
                    <path d="M237,209 L249,209 L249,197 L257,197 L243,183 L229,197 L237,197 L237,209 Z M229,213 L257,213 L257,217 L229,217 L229,213 Z"></path>
                  </g>
                </g>
              </svg>
            </div>
          )}
        </div>
        {uploading || mq.SM_AND_DOWN ? null : (
          <p className="avatar-text">Change your official id</p>
        )}
      </div>
    );
  };

  return (
    <div className={`avatar-dropzone ${styles.dropzoneContainer}`} {...getRootProps()}>
      <input {...getInputProps()} />
      <div
        onMouseEnter={mq.SM_AND_DOWN ? undefined : () => setHover(true)}
        onFocus={mq.SM_AND_DOWN ? undefined : () => setHover(true)}
        onMouseLeave={mq.SM_AND_DOWN ? undefined : () => setHover(false)}
        onBlur={mq.SM_AND_DOWN ? undefined : () => setHover(false)}
        onDragOver={() => setHover(true)}
        onDragLeave={() => setHover(false)}
        className={
          image ? styles.uploadButton : 'educator-account-settings-official-id-upload-default-image'
        }
      >
        {renderOverlay()}
        {image ? (
          <img alt="User avatar" className={styles.uploadedImage} src={`https:${image}`} />
        ) : (
          <div className="educator-account-settings-official-id-upload-no-image">
            <Icon iconType="upload" className="educator-account-settings-official-id-upload-icon" />
            <Text emphasis weight={3}>
              Drop your image here
            </Text>
            <Text weight={6} showOverflow>
              Upload a photo of your official government or school ID here
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default OfficialIdUploader;
